import { FolderOutlined, LoadingOutlined, StopOutlined, UploadOutlined } from "@ant-design/icons";
import { Affix, Button, Checkbox, Image, Input, List, Skeleton, Space, Tooltip, Typography } from "antd";
import { FilterValue } from "antd/lib/table/interface";
import { AxiosError } from "axios";
import OwnersDisplay from "components/OwnersDisplay";
import PackageSelector from "components/PackageSelector";
import CardTitleSearch from "components/staff/Cards/CardTitleSearch";
import StaffCardsFilter, { StaffCardFilterDetail } from "components/staff/Cards/StaffCardsFilter";
import CardQAToolbar from "components/staff/CardTitleQA/CardQAToolBar";
import CellDisplay from "components/staff/CardTitleQA/CellDisplay";
import GradeOrConditionSelector from "components/staff/CardTitleQA/GradeOrConditionSelector";
import PhotoTaker from "components/staff/CardTitleQA/PhotoTaker";
import FriendlyIdFilter from "components/staff/Packages/FriendlyIdFilter";
import { useIdentity } from "context/auth";
import {
    CardTitleCsvImport,
    CardTitleCsvImportStatus,
    CardTitleInQA,
    CardTitleValidationFailure,
    defaultLotCondition,
    defaultUngradedCondition,
    GameCategory,
    MaxTitleLength
} from "models/cardIngestion";
import { InitialPageRequest, PageRequest } from "models/common";
import useAssignedCardTitle from "pages/typer/useAssignCardTitle";
import { HTMLAttributes, Key, Suspense, useCallback, useState } from "react";
import { EditableColumnsType, EditableTable } from "util/editableTable";
import { createCustomFilter } from "util/table";
import { useBatchSearching, useBulkActions, useCardTitlePatching, useCardTitlesToQA, useReadyForUpload } from "./useCardTitlesToQA";

// ant.design puts tables in a wrapper div, this seems to be the only way to apply 
// the required border-collapse style so that the batch-break borders show up
import GradeOrConditionDisplay from "components/staff/CardTitleQA/GradeOrConditionDisplay";
import { PackageTypeWeight } from "models/packages";
import "styles/card_title_qa_table.css";

const previewImage = require("images/camera.png");

const StartOfNextBatchStyle: HTMLAttributes<never> = { style: { borderTop: '5px solid var(--dcs-blue)' } };

const StartOfSkippedBatchStyle: HTMLAttributes<never> = { style: { borderTop: '10px solid var(--dcs-warning)' } };

const CardTitleQADashboard = () => {
    const { isAdmin } = useIdentity();
    const [{ page, pageSize, withinDays, filterBy, sort }, setPageRequest] =
        useState<PageRequest>({ ...InitialPageRequest, pageSize: 40, filterBy: { "DoNotUpload": [false] } });
    const [filterDetail, setFilterDetail] = useState<StaffCardFilterDetail>({});
    const { isLoading, data: resp, refetch } = useCardTitlesToQA({ page, pageSize, withinDays, filterBy, sort });
    const { data = [], totalRecords } = resp || {};
    const { patchCardTitle, isPatching } = useCardTitlePatching();
    const { doNotUpload, allowUpload, isSaving } = useAssignedCardTitle();
    const { findBatch } = useBatchSearching();
    const { bulkPackageIdUpdate, bulkDoNotUploadUpdate, isBulkUpdating } = useBulkActions();
    const { checkBatches, batchesPassedQA, cardsPassedQA, isCompleting } = useReadyForUpload();
    const [selectedCardIds, setSelectedCardIds] = useState<Key[]>([]);
    const [foundCardId, setFoundCardId] = useState<string | null>(null);
    const [searchFailed, setSearchFailed] = useState(false);
    const [dnu, setDnu] = useState(false);
    const [affixed, setAffixed] = useState(false);
    const [errors, setErrors] = useState<CardTitleValidationFailure[]>([]);

    const working = isLoading || isPatching || isSaving || isBulkUpdating || isCompleting;

    const onFilterUpdate = (key: string) => (value: any) => {
        setFilterDetail((prev) => ({ ...prev, [key]: value }));
    };

    const columns: EditableColumnsType<CardTitleInQA> = [
        {
            title: "",
            key: "doNotUpload",
            render: (_, rec) => rec.doNotUpload ? (
                <Button
                    type="primary"
                    size="small"
                    icon={<StopOutlined />}
                    danger
                    style={{ marginLeft: '10px' }}
                    onClick={() => toggleDoNotUpload(rec.id, false)}
                    disabled={rec.title.length > MaxTitleLength || !rec.packageId}
                />
            ) : (
                <Button
                    type="primary"
                    size="small"
                    icon={<UploadOutlined />}
                    style={{ marginLeft: '10px', backgroundColor: 'var(--dcs-dark-green)' }}
                    onClick={() => toggleDoNotUpload(rec.id, true)}
                />
            )
        },
        {
            title: "Photos",
            key: "photos",
            align: 'center',
            render: (_, rec) => <Image.PreviewGroup>
                <Image
                    src={previewImage}
                    width={20}
                    preview={{ src: rec.photos[0].imageUrl }}
                    placeholder={<LoadingOutlined />}
                />
                <Image
                    style={{ display: 'none' }}
                    preview={{ src: rec.photos[1].imageUrl }}
                />
            </Image.PreviewGroup>
        },
        {
            title: "Customer(s)",
            key: "customers",
            width: '150px',
            render: (_, rec) => rec.owners.length === 0
                ? null
                : <OwnersDisplay owners={rec.owners} displayEmail={false} />,
        },
        {
            title: "Package ID",
            dataIndex: "packageFriendlyId",
            editable: true,
            render: (id, rec) => <Space>
                <CellDisplay
                    value={id}
                    errorIf={!id}
                />
                <Tooltip placement="top" title={rec.folderName}>
                    <FolderOutlined style={{ color: 'var(--dcs-blue)' }} />
                </Tooltip>
            </Space>,
            editRender: (row, save, cancel) => <PackageSelector
                autoFocus
                placeholder={row.packageFriendlyId?.toString() ?? "Package ID"}
                onSelected={(pkg) => save({
                    packageId: pkg?.id,
                    packageFriendlyId: pkg?.friendlyId
                })}
                onClear={cancel}
                onCancel={cancel}
                onBlur={cancel}
            />,
            // only include the filter when viewing "Do Not Upload" cards
            ...(dnu ? createCustomFilter(
                FriendlyIdFilter,
                onFilterUpdate("friendlyPackageId"),
                filterDetail.friendlyPackageId
            ) : {})
        },
        {
            title: "Photo Taker(s)",
            key: "photoTaker",
            render: (_, rec) => <PhotoTaker 
                pkgRecDate={rec.packageReceivedDate}
                photos={rec.photos} 
            />
        },
        {
            title: "Batch",
            dataIndex: "batch",
        },
        {
            title: "Card Title",
            dataIndex: "title",
            width: '700px',
            editable: true,
            handleDefaultEvents: false,
            rules: [{
                max: MaxTitleLength, message: `Card title has to be ${MaxTitleLength} characters or less`
            }],
            render: (title) => <CellDisplay
                value={title}
                errorIf={!title || title.length > MaxTitleLength}
            />,
            editRender: (row, save, cancel) => <Input
                autoFocus
                showCount={{ formatter: (args) => `${args.count} / ${MaxTitleLength}` }}
                spellCheck
                onBlur={e => {
                    if (e.target.value !== row.title) {
                        save();
                    } else {
                        cancel();
                    }
                }}
                onKeyDown={e => {
                    if (e.key === "Escape" || (e.key === "Enter" && e.currentTarget.value === row.title)) {
                        cancel();
                    } else if (e.key === "Enter") {
                        save();
                    }
                }}
            />,
            // only include the filter when viewing "Do Not Upload" cards
            ...(dnu ? createCustomFilter(
                CardTitleSearch,
                onFilterUpdate("title"),
                filterDetail.title
            ) : {})
        },
        {
            title: "Help / Notes",
            dataIndex: "notes",
            width: '300px',
            render: (_, rec) => <Typography.Text strong type="danger">
                {`${rec.notes ?? ''} ${rec.doNotUploadReason ?? ''}`}
            </Typography.Text>
        },
        {
            title: "Lot?",
            dataIndex: "isLot",
            editable: true,
            render: (isLot) => isLot ? "Yes" : "No",
            editRender: () => <Checkbox />,
            valuePropName: "checked"
        },
        {
            title: "Status / Grader",
            dataIndex: "statusName",
            render: (_, rec) => <GradeOrConditionDisplay {...rec} />,
            editable: true,
            handleDefaultEvents: false,
            editRender: (row, save, cancel) => <div tabIndex={0}
                onKeyDown={e => {
                    if (e.key === "Escape") {
                        cancel();
                    }
                }}
                onBlur={e => {
                    if (!e.currentTarget.contains(e.relatedTarget)) {
                        cancel();
                    }
                }}>
                <Suspense fallback={<Skeleton.Input active block size="large" />}>
                    <GradeOrConditionSelector
                        row={row}
                        save={save}
                        cancel={cancel}
                        defaultUngradedToExcellent={false}
                    />
                </Suspense>
            </div>
        }
    ];

    const searchForBatch = async (term: string) => {
        setSearchFailed(false);

        try {
            const result = await findBatch({ term, pageSize: pageSize! })

            setFoundCardId(result.cardId);
            setPageRequest({
                page: result.pageNumber,
                pageSize,
                filterBy,
                withinDays,
                sort
            });
            setSelectedCardIds([]);
        } catch (error) {
            setSearchFailed(true);
        }
    }

    const toggleDoNotUpload = async (cardId: string, doNotUploadState: boolean) => {
        if (doNotUploadState) {
            await doNotUpload({ cardId, reason: 'Do Not Upload' });
        } else {
            await allowUpload({ cardId, completeCardTitle: true });
        }

        await refetch();
    }

    const tableChanged = (
        // pagination
        { current: newPage, pageSize: newPageSize }: any,
        // filtering
        filters: Record<string, FilterValue | null>,
        // sorting
        { column, field, order }: any
    ) => {
        setPageRequest({
            page: newPageSize !== pageSize ? 1 : newPage,
            pageSize: newPageSize,
            filterBy: { ...filterBy, ...filters },
            withinDays,
            sort: column ? [field, order === "ascend"] : null,
        });
        setFoundCardId(null);
        setSelectedCardIds([]);
    };

    const cascadingUpdates = useCallback((newRow: CardTitleInQA, dataIndex: string) => {
        const originalRow = data?.find(x => x.id === newRow.id)!;

        // isLot changed, default certain row properties accordingly
        if (dataIndex === 'isLot' && originalRow.isLot !== newRow.isLot) {
            // the default condition varies based on whether or not the category is 'Game'
            const isGame = newRow.category === GameCategory;

            return {
                ...(newRow.isLot
                    ? defaultLotCondition()
                    : defaultUngradedCondition(isGame))
            }
        }

        return {};
    }, [data]);

    const handleSave = useCallback(async (
        row: CardTitleInQA,
        partial: Partial<CardTitleInQA>,
        dataIndex: keyof CardTitleInQA
    ) => {
        const extraUpdates = cascadingUpdates(row, dataIndex);
        const payload = { id: row.id, ...partial, ...extraUpdates };

        await patchCardTitle(payload);
        await refetch();
    }, [refetch, patchCardTitle, cascadingUpdates]);

    const onBulkPackageIdUpdate = async (packageId: string) => {
        await bulkPackageIdUpdate({
            packageId,
            cardIds: selectedCardIds.map(c => c.toString())
        });
        await refetch();
        setSelectedCardIds([]);
    }

    const onBulkDoNotUpload = async () => {
        await bulkDoNotUploadUpdate(selectedCardIds.map(c => c.toString()));
        await refetch();
        setSelectedCardIds([]);
    }

    const onCheckBatches = async (startBatch: string, endBatch: string) => {
        return await checkBatches({ startBatch, endBatch });
    }

    const onBatchesPassedQA = async (startBatch: string, endBatch: string) => {
        setErrors([]);

        try {
            await batchesPassedQA({ startBatch, endBatch });
            setSelectedCardIds([]);
        } catch (e) {
            if (e instanceof AxiosError) {
                if (e.response?.data?.title === undefined) {
                    const errors = e.response?.data as CardTitleValidationFailure[]
                    setErrors(errors);
                } else {
                    throw e;
                }
            }
        } finally {
            await refetch();
        }
    }

    const onCardsPassedQA = async () => {
        setErrors([]);

        try {
            await cardsPassedQA(selectedCardIds.map(c => c.toString()));

            setSelectedCardIds([]);
        } catch (e) {
            if (e instanceof AxiosError) {
                const errors = e.response?.data as CardTitleValidationFailure[]
                setErrors(errors);
            }
        } finally {
            await refetch();
        }
    }

    const onCsvImportFinished = async (rec: CardTitleCsvImport) => {
        if (rec.status === CardTitleCsvImportStatus.Completed) {
            await refetch();
        }
    }

    const onPackageTypeChanged = (packageTypeId: string | null) => {
        delete filterBy!["PackageTypeId"];
        setPageRequest({
            page: 1,
            pageSize,
            withinDays,
            filterBy: packageTypeId !== null
                ? { ...filterBy, "PackageTypeId": [packageTypeId] }
                : { ...filterBy },
            sort
        });
        setFoundCardId(null);
        setSelectedCardIds([]);
    }

    const onPackageWeightChanged = (weight: PackageTypeWeight | null) => {
        delete filterBy!["SortingWeight"];
        setPageRequest({
            page: 1,
            pageSize,
            withinDays,
            filterBy: weight !== null
                ? { ...filterBy, "SortingWeight": [weight] }
                : { ...filterBy },
            sort
        });
        setFoundCardId(null);
        setSelectedCardIds([]);
    }

    const onViewTypeChanged = (doNotUpload: boolean, fixesNeeded: boolean) => {
        const { "PackageTypeId": packageTypeId } = filterBy ?? {};
        const { "SortingWeight": weight } = filterBy ?? {};
        setFilterDetail({});
        setPageRequest({
            page: 1,
            pageSize,
            withinDays,
            filterBy: {
                "DoNotUpload": [doNotUpload],
                "PackageTypeId": packageTypeId,
                "SortingWeight": weight,
                "FixesNeeded": [fixesNeeded]
            },
            sort
        });
        setDnu(doNotUpload);
        setSelectedCardIds([]);
    }

    const onUpdatedRules = async () => {
        await refetch();
    }

    const batchDiff = (rec: CardTitleInQA, idx: number) => {
        if (idx === 0 || !data) {
            return 0;
        }

        if (data[idx - 1].batch === rec.batch) {
            return 0;
        }

        const curNum = batchPosition(rec.batch);
        const prevNum = batchPosition(data[idx - 1].batch);

        if (isNaN(curNum) || isNaN(prevNum)) {
            return 0;
        }

        return Math.abs(curNum - prevNum);
    }

    const batchPosition = (batchNumber: string) => {
        const match = batchNumber.match(/[0-9]{1,3}/);
        if (!match) {
            return NaN;
        }

        return Number(match[0]);
    }

    const highlightRow = (rec: CardTitleInQA, idx: number | undefined): HTMLAttributes<never> => {
        const diff = batchDiff(rec, idx ?? 0);
        const baseStyle = diff === 0
            ? {}
            : diff === 1
                ? StartOfNextBatchStyle
                : StartOfSkippedBatchStyle;

        if (rec.doNotUpload) {
            return { style: { ...baseStyle, backgroundColor: 'rgba(from var(--dcs-error-red) r g b / 0.2)' } };
        } else if (rec.id === foundCardId) {
            return { style: { ...baseStyle, backgroundColor: 'rgba(from var(--dcs-dark-green) r g b / 0.1)' } };
        }

        return { ...baseStyle };
    }

    const errorKeys = [...new Set([
        // validationErrors will only be populated when on the "Fixes Needed" view
        ...data.filter(x => x.validationErrors !== null).map(x => x.id),
        ...errors.map(e => e.cardId)
    ])];

    return <EditableTable
        size="small"
        className="card-title-qa-table"
        title={() =>
            <Space direction="vertical" style={{ marginBottom: '5px' }}>
                <Affix onChange={a => setAffixed(a ?? false)} style={affixed ? { width: `${document.body.clientWidth - 15}px` } : {}}>
                    <div style={{ backgroundColor: 'white', ...(affixed ? { boxShadow: '0px 5px 20px black' } : {}) }}>
                        <StaffCardsFilter detail={filterDetail} />
                        <CardQAToolbar
                            isAdmin={isAdmin ?? false}
                            onSearch={async (term: string) => await searchForBatch(term)}
                            onSearchClear={() => setSearchFailed(false)}
                            searchFailed={searchFailed}
                            selectedRowCount={selectedCardIds.length}
                            onBulkDoNotUpload={onBulkDoNotUpload}
                            onBulkPackageIdUpdate={onBulkPackageIdUpdate}
                            onConfirmBatches={onCheckBatches}
                            onBatchesPassedQA={onBatchesPassedQA}
                            onCardsPassedQA={onCardsPassedQA}
                            onCsvImportFinished={onCsvImportFinished}
                            onPackageTypeChanged={onPackageTypeChanged}
                            onPackageWeightChanged={onPackageWeightChanged}
                            onUpdatedRules={onUpdatedRules}
                            onViewTypeChanged={onViewTypeChanged}
                            showJumpers={affixed}
                        />
                    </div>
                </Affix>
            </Space>
        }
        rowClassName={(rec, idx) => batchDiff(rec, idx) !== 0 ? 'break' : ''}
        loading={working}
        columns={columns}
        dataSource={data}
        rowKey={r => r.id}
        onRow={highlightRow}
        rowSelection={{
            selectedRowKeys: selectedCardIds,
            onChange: (keys) => setSelectedCardIds(keys),
            preserveSelectedRowKeys: true,
            getCheckboxProps: rec => ({
                disabled: rec.doNotUpload
            })
        }}
        onRowUpdate={handleSave}
        pagination={{
            total: totalRecords,
            current: page,
            pageSize,
            pageSizeOptions: [40, 80, 200, 400],
            showSizeChanger: true,
            showQuickJumper: true,
            position: ['topRight', 'bottomRight']
        }}
        onChange={tableChanged}
        expandable={{
            rowExpandable: r => errorKeys.some(k => k === r.id),
            expandedRowRender: r => {
                const err = (r.validationErrors ?? errors.find(e => e.cardId === r.id))!;
                return <List
                    size="small"
                    dataSource={err.errors}
                    style={{ marginLeft: '100px' }}
                    renderItem={item => <List.Item>
                        <Typography.Text type="danger" strong>{item}</Typography.Text>
                    </List.Item>}
                />;
            },
            expandedRowKeys: errorKeys
        }}
    />;
}

export default CardTitleQADashboard;